import { useEffect, useState } from "react";
import {
  Box, Button, Flex, FormControl, FormLabel, HStack, IconButton,
  Input, Select, SimpleGrid, Spinner, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { FaInbox, FaMoneyBillWave, FaRegTrashAlt } from "react-icons/fa";
import { ProcessNumerary } from "../../../models/Financial";
import { deleteProcessNumerary, getProcessNumeraries, saveProcessNumerary } from "../../../services/financialService";
import { crm, Pill } from "../../../theme";
import SectionCard from "./SectionCard";
import { brl, toneColors } from "./duimp";
import { tdProps, thProps } from "./tableStyles";

const blank = (): Partial<ProcessNumerary> => ({ title: "NUMERÁRIO" });

// Espelha CVF.Domain.Enums.Position (backend): 2=Administration, 3=Operational, 5=Financial.
// Autorização real é sempre validada no servidor; isto só controla a exibição do formulário.
// Se o enum Position for reordenado no backend, atualizar esta lista também.
const FINANCIAL_WRITE_POSITIONS = [2, 3, 5];

const inputProps = {
  h: "42px",
  borderRadius: "10px",
  borderColor: crm.border,
  fontSize: "14px",
  bg: crm.surface,
  _hover: { borderColor: crm.borderStrong },
  focusBorderColor: crm.brand,
};

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="6px">
    {children}
  </FormLabel>
);

const date = (value?: string) => (value ? DateTime.fromISO(value).toFormat("dd/LL/yyyy") : "—");

const NumeraryPanel = ({ processId }: { processId: string }) => {
  const [items, setItems] = useState<ProcessNumerary[]>([]);
  const [draft, setDraft] = useState<Partial<ProcessNumerary>>(blank());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const auth = useAuthUser<any>();
  const canWrite = Boolean(auth?.admin || FINANCIAL_WRITE_POSITIONS.includes(auth?.position));
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try { setItems(await getProcessNumeraries(processId)); }
    catch { toast({ status: "error", title: "Não foi possível carregar os numerários" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [processId]);

  const save = async () => {
    setSaving(true);
    try {
      await saveProcessNumerary(processId, { ...draft, amountBrl: draft.amountBrl == null ? undefined : Number(draft.amountBrl) });
      setDraft(blank());
      await load();
      toast({ status: "success", title: "Numerário salvo" });
    } catch { toast({ status: "error", title: "Não foi possível salvar o numerário" }); }
    finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try { await deleteProcessNumerary(processId, id); await load(); }
    catch { toast({ status: "error", title: "Não foi possível excluir o numerário" }); }
  };

  const totalPaid = items.filter((i) => i.paymentStatus === "paid").reduce((s, i) => s + (i.amountBrl ?? 0), 0);
  const totalOpen = items.filter((i) => i.paymentStatus !== "paid").reduce((s, i) => s + (i.amountBrl ?? 0), 0);

  return (
    <SectionCard
      icon={<FaMoneyBillWave />}
      title="Numerários"
      subtitle="Adiantamentos e tributos do processo"
      actions={
        items.length > 0 && (
          <>
            <Pill {...toneColors.success}>Pago {brl(totalPaid)}</Pill>
            {totalOpen > 0 && <Pill {...toneColors.warning}>Em aberto {brl(totalOpen)}</Pill>}
          </>
        )
      }
    >
      {loading ? (
        <Flex justify="center" py="24px"><Spinner color={crm.brand} /></Flex>
      ) : items.length === 0 ? (
        <Flex direction="column" align="center" py="24px" color={crm.textFaint} gap="8px">
          <FaInbox fontSize="28px" />
          <Text fontSize="13px" color={crm.textMuted}>Nenhum numerário lançado.</Text>
        </Flex>
      ) : (
        <Box border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm} overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th {...thProps}>Título</Th>
                <Th {...thProps}>Vencimento</Th>
                <Th {...thProps}>Status</Th>
                <Th {...thProps} isNumeric>Valor</Th>
                {canWrite && <Th {...thProps} w="48px" />}
              </Tr>
            </Thead>
            <Tbody>
              {items.map((item) => {
                const isDuimp = item.sourceType === "duimp";
                const selected = draft.id === item.id;
                return (
                  <Tr
                    key={item.id}
                    role="group"
                    cursor={canWrite ? "pointer" : undefined}
                    bg={selected ? crm.brandSoft : undefined}
                    _hover={{ bg: crm.brandSoft }}
                    onClick={() => canWrite && setDraft(item)}
                  >
                    <Td {...tdProps}>
                      <HStack spacing="8px">
                        <Text fontWeight="600">{isDuimp ? item.title.replace(/\s*-\s*DUIMP$/i, "") : item.title}</Text>
                        {isDuimp && (
                          <Tooltip label="Lançado automaticamente a partir da DUIMP (Portal Único)" hasArrow>
                            <Box><Pill {...toneColors.info}>DUIMP</Pill></Box>
                          </Tooltip>
                        )}
                        {item.isForecast && <Pill>Previsto</Pill>}
                      </HStack>
                    </Td>
                    <Td {...tdProps} color={crm.textMuted}>{date(item.dueDate)}</Td>
                    <Td {...tdProps}>
                      {item.paymentStatus === "paid" ? (
                        <Pill {...toneColors.success}>Pago{item.paidAt ? ` em ${date(item.paidAt)}` : ""}</Pill>
                      ) : (
                        <Pill {...toneColors.warning}>Não pago</Pill>
                      )}
                    </Td>
                    <Td {...tdProps} isNumeric fontWeight="600">{brl(item.amountBrl)}</Td>
                    {canWrite && (
                      <Td {...tdProps} textAlign="right">
                        <IconButton
                          aria-label="Excluir numerário"
                          icon={<FaRegTrashAlt />}
                          size="xs"
                          variant="ghost"
                          color={crm.textFaint}
                          opacity={0}
                          _groupHover={{ opacity: 1 }}
                          _hover={{ color: crm.danger, bg: "#fef2f2" }}
                          onClick={(event) => { event.stopPropagation(); remove(item.id); }}
                        />
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {canWrite && (
        <Box mt="20px" p="16px" bg={crm.subtle} border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm}>
          <Text fontSize="13px" fontWeight="700" color={crm.text} mb="12px">
            {draft.id ? "Editar numerário" : "Novo numerário"}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 3, xl: 6 }} spacing="12px">
            <FormControl gridColumn={{ md: "span 3", xl: "span 2" }}>
              <Label>Título</Label>
              <Input {...inputProps} value={draft.title ?? ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </FormControl>
            <FormControl>
              <Label>Valor (R$)</Label>
              <Input {...inputProps} type="number" step="0.01" value={draft.amountBrl ?? ""} onChange={(e) => setDraft({ ...draft, amountBrl: e.target.value === "" ? undefined : Number(e.target.value) })} />
            </FormControl>
            <FormControl>
              <Label>Vencimento</Label>
              <Input {...inputProps} type="date" value={draft.dueDate?.slice(0, 10) ?? ""} onChange={(e) => setDraft({ ...draft, dueDate: e.target.value ? `${e.target.value}T00:00:00` : undefined })} />
            </FormControl>
            <FormControl>
              <Label>Status</Label>
              <Select {...inputProps} value={draft.paymentStatus ?? ""} onChange={(e) => setDraft({ ...draft, paymentStatus: e.target.value as "paid" | "unpaid" | undefined, paidAt: e.target.value === "paid" ? draft.paidAt : undefined })}>
                <option value="" disabled>Selecione</option>
                <option value="unpaid">Não pago</option>
                <option value="paid">Pago</option>
              </Select>
            </FormControl>
            <FormControl>
              <Label>Pago em</Label>
              <Input {...inputProps} type="date" isDisabled={draft.paymentStatus !== "paid"} value={draft.paidAt?.slice(0, 10) ?? ""} onChange={(e) => setDraft({ ...draft, paidAt: e.target.value ? `${e.target.value}T00:00:00` : undefined })} />
            </FormControl>
          </SimpleGrid>
          <Flex mt="12px" justify="space-between" align="flex-end" gap="12px" wrap="wrap">
            <FormControl maxW="220px">
              <Label>Tipo de lançamento</Label>
              <Select {...inputProps} value={draft.isForecast == null ? "" : String(draft.isForecast)} onChange={(e) => setDraft({ ...draft, isForecast: e.target.value === "" ? undefined : e.target.value === "true" })}>
                <option value="" disabled>Selecione</option>
                <option value="false">Confirmado</option>
                <option value="true">Previsto</option>
              </Select>
            </FormControl>
            <HStack>
              <Button variant="outline" borderColor={crm.border} color={crm.text} borderRadius="10px" _hover={{ bg: crm.brandSoft }} onClick={() => setDraft(blank())}>
                {draft.id ? "Cancelar" : "Limpar"}
              </Button>
              <Button bg={crm.brand} color="white" borderRadius="10px" _hover={{ bg: crm.brandDark }} isLoading={saving} onClick={save}>
                {draft.id ? "Salvar alterações" : "Adicionar"}
              </Button>
            </HStack>
          </Flex>
        </Box>
      )}
    </SectionCard>
  );
};

export default NumeraryPanel;
